// CustomProductOptionsクラスを定義
class CustomProductOptions {
  // product id
  static EMBROIDERY_PRODUCT_ID = 7761118429273;
  static HANAKOMON_PRODUCT_ID = 7761122951257;

  static EMBROIDERY_HANDLE = "option-shishu";
  static HANAKOMON_HANDLE = "option-hanakomon";

  constructor() {
    this.embroideryCartId = null; // 刺繍のカートline item id
    this.hanakomonCartId = null; // 花個紋のカートline item id
    this.embroideryCartKey = null; // 刺繍のカートline item key
    this.hanakomonCartKey = null; // 花個紋のカートline item key
    this.embroideryPrice = 0; // 刺繍の価格
    this.hanakomonPrice = 0; // 花個紋の価格

    // 処理制御用のプロパティ
    this.processingQueue = []; // 処理キュー
    this.isProcessing = false; // 処理中フラグ
    this.embroideryProcessing = false; // 刺繍処理中フラグ
    this.hanakomonProcessing = false; // 花個紋処理中フラグ
    this.maxRetries = 3; // 最大リトライ回数
  }

  // キューに処理を追加して順次実行
  async queueOperation(operationType, operation, retryCount = 0) {
    return new Promise((resolve, reject) => {
      this.processingQueue.push({
        operationType,
        operation,
        resolve,
        reject,
        retryCount,
      });

      if (!this.isProcessing) {
        this.processQueue();
      }
    });
  }

  // キューを処理
  async processQueue() {
    if (this.isProcessing || this.processingQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.processingQueue.length > 0) {
      const { operationType, operation, resolve, reject, retryCount } =
        this.processingQueue.shift();

      try {
        // 処理中フラグを設定
        if (operationType === "embroidery") {
          this.embroideryProcessing = true;
        } else if (operationType === "hanakomon") {
          this.hanakomonProcessing = true;
        }

        const result = await operation();
        resolve(result);
      } catch (error) {
        console.error(`${operationType}処理エラー:`, error);

        // リトライ可能な場合
        if (retryCount < this.maxRetries) {
          console.log(
            `${operationType}処理をリトライします (${retryCount + 1}/${this.maxRetries
            })`
          );
          this.processingQueue.unshift({
            operationType,
            operation,
            resolve,
            reject,
            retryCount: retryCount + 1,
          });
          // 少し待ってからリトライ
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * (retryCount + 1))
          );
        } else {
          reject(error);
        }
      } finally {
        // 処理中フラグを解除
        if (operationType === "embroidery") {
          this.embroideryProcessing = false;
        } else if (operationType === "hanakomon") {
          this.hanakomonProcessing = false;
        }
      }
    }

    this.isProcessing = false;
  }

  // 状態の整合性をチェック
  validateState(operationType) {
    if (operationType === "embroidery") {
      return !this.embroideryProcessing;
    } else if (operationType === "hanakomon") {
      return !this.hanakomonProcessing;
    }
    return true;
  }

  // 刺繍ステータスを更新（ON:カート追加, OFF:カート削除）
  async setEmbroidery(status, options, updateLineItemId = false) {
    // 処理中かチェック
    if (this.embroideryProcessing) {
      console.log("刺繍処理中です。キューに追加します。");
    }

    return this.queueOperation("embroidery", async () => {
      return await this._setEmbroideryInternal(
        status,
        options,
        updateLineItemId
      );
    });
  }

  // 内部の刺繍設定処理
  async _setEmbroideryInternal(status, options, updateLineItemId = false) {
    if (status) {
      console.log("setEmbroidery", options);
      if (updateLineItemId) {
        const removed = await this.removeFromCart(this.embroideryCartKey);
        console.log("removed", removed);
        if (removed && removed.items && removed.items.length > 0) {
          this.embroideryCartId = null;
          this.embroideryCartKey = null;
        }
      }
      // ON: カートに追加し、line item idを保存
      const trimmedText = options.embroidery.text.replace(/[\s\u3000]/g, "");
      const textLength = trimmedText.length;
      const orientation = options.embroidery.orientation;
      const embroideryVariant = await this.getEmbroideryVariant(
        textLength,
        orientation
      );
      const properties = {
        文字: options.embroidery.text,
        色: options.embroidery.color,
        刺繍を入れる商品: CURRENT_PRODUCT.title,
        刺繍を入れる面: options.embroidery.target_item_pattern,
      };
      if (options.embroidery.note) {
        properties["備考"] = options.embroidery.note;
      }
      const item = {
        id: embroideryVariant.id,
        quantity: 1,
        properties: properties,
      };
      const added = await this.addToCartWithResponse([item]);
      console.log("added", added, textLength);
      if (added && added.items && added.items.length > 0) {
        this.embroideryCartId = added.items[0].id;
        this.embroideryCartKey = added.items[0].key;
        this.embroideryPrice = embroideryVariant.price / 100;
        return this.getPrice();
      }
    } else {
      // OFF: カートから削除
      if (this.embroideryCartId) {
        const removed = await this.removeFromCart(this.embroideryCartId);
        console.log("removed", removed);
        if (removed) {
          this.embroideryCartId = null;
          this.embroideryCartKey = null;
          this.embroideryPrice = 0;
          return this.getPrice();
        }
      }
    }
  }

  // 花個紋ステータスを更新（ON:カート追加, OFF:カート削除）
  async setHanakomon(status, options) {
    // 処理中かチェック
    if (this.hanakomonProcessing) {
      console.log("花個紋処理中です。キューに追加します。");
    }

    return this.queueOperation("hanakomon", async () => {
      return await this._setHanakomonInternal(status, options);
    });
  }

  // 内部の花個紋設定処理
  async _setHanakomonInternal(status, options) {
    if (status) {
      const hanakomonDate = options.hanakomon.date;
      const hanakomonTitle = options.hanakomon.title;
      const hanakomonColor = options.hanakomon.color;
      const hanakomonVariant = await this.getHanakomonVariant();
      const properties = {
        日付: `${hanakomonDate}`,
        色: hanakomonColor,
        花個紋を入れる商品: CURRENT_PRODUCT.title,
        花個紋を入れる面: options.hanakomon.target_item_pattern,
      };
      if (options.hanakomon.note) {
        properties["備考"] = options.hanakomon.note;
      }
      const item = {
        id: hanakomonVariant.id,
        quantity: 1,
        properties: properties,
      };
      const added = await this.addToCartWithResponse([item]);
      console.log("added hanakomon", added);
      if (added && added.items && added.items.length > 0) {
        this.hanakomonCartId = added.items[0].id;
        this.hanakomonCartKey = added.items[0].key;
        this.hanakomonPrice = hanakomonVariant.price / 100;
        return this.getPrice();
      }
    } else {
      if (this.hanakomonCartId) {
        const removed = await this.removeFromCart(this.hanakomonCartId);
        console.log("removed hanakomon", removed);
        if (removed) {
          this.hanakomonCartId = null;
          this.hanakomonCartKey = null;
          this.hanakomonPrice = 0;
          return this.getPrice();
        }
      }
    }
  }

  async updateEmbroidery(options) {
    // 処理中かチェック
    if (this.embroideryProcessing) {
      console.log("刺繍処理中です。キューに追加します。");
    }

    return this.queueOperation("embroidery", async () => {
      return await this._updateEmbroideryInternal(options);
    });
  }

  // 内部の刺繍更新処理
  async _updateEmbroideryInternal(options) {
    const properties = {
      文字: options.embroidery.text,
      色: options.embroidery.color,
      刺繍を入れる商品: CURRENT_PRODUCT.title,
      刺繍を入れる面: options.embroidery.target_item_pattern,
    };
    if (options.embroidery.note) {
      properties["備考"] = options.embroidery.note;
    }
    const response = await this.updateCartProperties(
      this.embroideryCartKey,
      properties
    );
    console.log("updateEmbroidery", response);
    if (response && response.items && response.items.length > 0) {
      this.embroideryCartKey = response.items.find(
        (item) => item.id === this.embroideryCartId
      ).key;
    }
    return response;
  }

  async updateHanakomon(options) {
    // 処理中かチェック
    if (this.hanakomonProcessing) {
      console.log("花個紋処理中です。キューに追加します。");
    }

    return this.queueOperation("hanakomon", async () => {
      return await this._updateHanakomonInternal(options);
    });
  }

  // 内部の花個紋更新処理
  async _updateHanakomonInternal(options) {
    const properties = {
      日付: `${options.hanakomon.date}`,
      色: options.hanakomon.color,
      花個紋を入れる商品: CURRENT_PRODUCT.title,
      花個紋を入れる面: options.hanakomon.target_item_pattern,
    };
    if (options.hanakomon.note) {
      properties["備考"] = options.hanakomon.note;
    }
    const response = await this.updateCartProperties(
      this.hanakomonCartKey,
      properties
    );
    console.log("updateHanakomon", response);
    if (response && response.items && response.items.length > 0) {
      this.hanakomonCartKey = response.items.find(
        (item) => item.id === this.hanakomonCartId
      ).key;
    }
    return response;
  }

  // キューをクリア
  clearQueue() {
    this.processingQueue = [];
    this.isProcessing = false;
    this.embroideryProcessing = false;
    this.hanakomonProcessing = false;
    console.log("処理キューをクリアしました");
  }

  // キュー状態を取得
  getQueueStatus() {
    return {
      queueLength: this.processingQueue.length,
      isProcessing: this.isProcessing,
      embroideryProcessing: this.embroideryProcessing,
      hanakomonProcessing: this.hanakomonProcessing,
      maxRetries: this.maxRetries,
    };
  }

  // ステータス取得
  getStatus() {
    return {
      embroideryCartId: this.embroideryCartId,
      hanakomonCartId: this.hanakomonCartId,
      queueStatus: this.getQueueStatus(),
    };
  }
  // どちらかが選択されているかどうか
  isAnySelected() {
    return !!this.embroideryCartId || !!this.hanakomonCartId;
  }

  getPrice() {
    return {
      embroideryPrice: this.embroideryPrice,
      hanakomonPrice: this.hanakomonPrice,
      totalPrice: this.embroideryPrice + this.hanakomonPrice,
    };
  }

  // 刺繍はテキスト方向、文字数によって
  // variantID を取得し、返す
  async getEmbroideryVariant(textLength, orientation) {
    const handle = CustomProductOptions.EMBROIDERY_HANDLE;
    const textLengthValue = textLength > 6 ? textLength : "1~6";
    const variant = await fetch(
      `${window.Shopify.routes.root}products/${handle}.js`
    )
      .then((r) => r.json())
      .then((p) => {
        console.log("variants", p);
        return p.variants.find(
          (v) => v.option1 === orientation && v.option2 == textLengthValue
        );
      });
    console.log("variant", variant);
    return variant;
  }

  // 花個紋のvariantIDを取得
  async getHanakomonVariant() {
    const handle = CustomProductOptions.HANAKOMON_HANDLE;
    const variant = await fetch(
      `${window.Shopify.routes.root}products/${handle}.js`
    )
      .then((r) => r.json())
      .then((p) => {
        return p.variants[0];
      });
    return variant;
  }

  // カートに商品を追加する（Shopify Ajax API例）
  addToCart(items) {
    console.log("addToCart", items);
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items: items }),
    })
      .then((response) => response.json())
      .then((data) => {
        // 追加後の処理（例：カートを更新、通知表示など）
        console.log("カートに追加されました", data);
      })
      .catch((error) => {
        console.error("カート追加エラー:", error);
      });
  }

  async addOptionToCart() {
    const embroideryVariant = await this.getEmbroideryVariant(3, "縦書き");
    const items = [
      {
        id: embroideryVariant.id,
        quantity: 1,
      },
    ];
    this.addToCart(items);
  }

  // カートに商品を追加し、レスポンスを返す
  async addToCartWithResponse(items) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: items }),
      });
      return await response.json();
    } catch (error) {
      console.error("カート追加エラー:", error);
      return null;
    }
  }

  // カートからline itemを削除
  async removeFromCart(lineItemId) {
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: lineItemId,
          quantity: 0,
        }),
      });
      return await response.json();
    } catch (error) {
      console.error("カート削除エラー:", error);
      return null;
    }
  }

  // line itemのpropertyを更新
  async updateCartProperties(lineItemId, properties) {
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: lineItemId,
          properties: properties,
        }),
      });
      return await response.json();
    } catch (error) {
      console.error("カートプロパティ更新エラー:", error);
    }
  }
}
